import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type {
  ChampCustomType,
  EtapeType,
  Json,
  PipelineChampCustomRow,
  PipelineEtapeRow,
  PipelineMotifPerteRow,
  PipelineRow,
} from '@/lib/database.types';

export function usePipelines(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['crm_pipelines', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<PipelineRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('pipelines')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('ordre');
      if (error) throw error;
      return data as unknown as PipelineRow[];
    },
  });
}

export function usePipeline(pipelineId: string | null) {
  return useQuery({
    queryKey: ['crm_pipeline', pipelineId],
    enabled: !!pipelineId,
    queryFn: async (): Promise<PipelineRow | null> => {
      if (!pipelineId) return null;
      const { data, error } = await supabase
        .from('pipelines')
        .select('*')
        .eq('id', pipelineId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as PipelineRow | null) ?? null;
    },
  });
}

export function usePipelineEtapes(pipelineId: string | null) {
  return useQuery({
    queryKey: ['crm_etapes', pipelineId],
    enabled: !!pipelineId,
    queryFn: async (): Promise<PipelineEtapeRow[]> => {
      if (!pipelineId) return [];
      const { data, error } = await supabase
        .from('pipeline_etapes')
        .select('*')
        .eq('pipeline_id', pipelineId)
        .order('ordre');
      if (error) throw error;
      return data as unknown as PipelineEtapeRow[];
    },
  });
}

export function usePipelineChamps(pipelineId: string | null) {
  return useQuery({
    queryKey: ['crm_champs', pipelineId],
    enabled: !!pipelineId,
    queryFn: async (): Promise<PipelineChampCustomRow[]> => {
      if (!pipelineId) return [];
      const { data, error } = await supabase
        .from('pipeline_champs_custom')
        .select('*')
        .eq('pipeline_id', pipelineId)
        .order('ordre');
      if (error) throw error;
      return data as unknown as PipelineChampCustomRow[];
    },
  });
}

export function usePipelineMotifsPerte(pipelineId: string | null) {
  return useQuery({
    queryKey: ['crm_motifs_perte', pipelineId],
    enabled: !!pipelineId,
    queryFn: async (): Promise<PipelineMotifPerteRow[]> => {
      if (!pipelineId) return [];
      const { data, error } = await supabase
        .from('pipeline_motifs_perte')
        .select('*')
        .eq('pipeline_id', pipelineId)
        .order('ordre');
      if (error) throw error;
      return data as unknown as PipelineMotifPerteRow[];
    },
  });
}

// ============== Mutations admin ==============

export function useUpsertPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      entreprise_id: string;
      activite_id?: string | null;
      nom: string;
      description?: string | null;
      couleur?: string;
      icone?: string | null;
      ordre?: number;
      actif?: boolean;
    }): Promise<PipelineRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('pipelines')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as PipelineRow;
      }
      const { data, error } = await supabase.from('pipelines').insert(rest).select('*').single();
      if (error) throw error;
      return data as unknown as PipelineRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm_pipelines'] });
      void qc.invalidateQueries({ queryKey: ['crm_pipeline'] });
    },
  });
}

export function useDeletePipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pipelineId: string): Promise<void> => {
      // Refus si opportunités existent
      const { count } = await supabase
        .from('opportunites')
        .select('id', { count: 'exact', head: true })
        .eq('pipeline_id', pipelineId);
      if ((count ?? 0) > 0)
        throw new Error(
          `Impossible : ${count} opportunité(s) liée(s). Désactivez plutôt que supprimer.`,
        );
      const { error } = await supabase.from('pipelines').delete().eq('id', pipelineId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_pipelines'] }),
  });
}

export function useUpsertEtape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      pipeline_id: string;
      nom: string;
      ordre: number;
      type: EtapeType;
      couleur?: string | null;
      icone_emoji?: string | null;
      probabilite_defaut?: number;
      auto_relance_apres_jours?: number | null;
      auto_archive_apres_jours?: number | null;
    }): Promise<PipelineEtapeRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('pipeline_etapes')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as PipelineEtapeRow;
      }
      const { data, error } = await supabase
        .from('pipeline_etapes')
        .insert(rest)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as PipelineEtapeRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm_etapes'] });
    },
  });
}

export function useDeleteEtape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (etapeId: string): Promise<void> => {
      const { count } = await supabase
        .from('opportunites')
        .select('id', { count: 'exact', head: true })
        .eq('etape_id', etapeId);
      if ((count ?? 0) > 0)
        throw new Error(`Étape contient ${count} opportunité(s). Migrez-les avant suppression.`);
      const { error } = await supabase.from('pipeline_etapes').delete().eq('id', etapeId);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_etapes'] }),
  });
}

export function useReorderEtapes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; pipelineId: string }): Promise<void> => {
      // Update ordre par lot
      await Promise.all(
        input.ids.map((id, idx) =>
          supabase
            .from('pipeline_etapes')
            .update({ ordre: idx + 1 })
            .eq('id', id),
        ),
      );
    },
    onSuccess: (_data, vars) =>
      void qc.invalidateQueries({ queryKey: ['crm_etapes', vars.pipelineId] }),
  });
}

export function useUpsertChamp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      pipeline_id: string;
      nom_champ: string;
      label: string;
      type: ChampCustomType;
      options?: Json | null;
      obligatoire?: boolean;
      ordre?: number;
      section?: string | null;
      description?: string | null;
    }): Promise<PipelineChampCustomRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('pipeline_champs_custom')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as PipelineChampCustomRow;
      }
      const { data, error } = await supabase
        .from('pipeline_champs_custom')
        .insert(rest)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as PipelineChampCustomRow;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_champs'] }),
  });
}

export function useDeleteChamp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('pipeline_champs_custom').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_champs'] }),
  });
}

export function useUpsertMotifPerte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      pipeline_id: string;
      libelle: string;
      ordre?: number;
    }): Promise<PipelineMotifPerteRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('pipeline_motifs_perte')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as PipelineMotifPerteRow;
      }
      const { data, error } = await supabase
        .from('pipeline_motifs_perte')
        .insert(rest)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as PipelineMotifPerteRow;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_motifs_perte'] }),
  });
}

export function useDeleteMotifPerte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('pipeline_motifs_perte').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['crm_motifs_perte'] }),
  });
}
